import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import FeeManagement from './FeeManagement';

const REGIONS = {
  EU: { label: 'Europe', currency: 'EUR', symbol: '€', locale: 'de-DE' },
  AU: { label: 'Australia', currency: 'AUD', symbol: 'A$', locale: 'en-AU' },
  US: { label: 'United States', currency: 'USD', symbol: '$', locale: 'en-US' }
};

const PRICING_URLS = {
  eventbrite: {
    EU: 'https://www.eventbrite.co.uk/pricing/',
    AU: 'https://www.eventbrite.com.au/pricing/',
    US: 'https://www.eventbrite.com/pricing/'
  },
  more: {
    EU: 'https://www.more.com/en/',
    AU: 'https://www.more.com/en/',
    US: 'https://www.more.com/en/'
  },
  humanitix: {
    EU: 'https://www.humanitix.com/gb/pricing',
    AU: 'https://www.humanitix.com/au/pricing',
    US: 'https://www.humanitix.com/us/pricing'
  },
  trybooking: {
    EU: 'https://www.trybooking.com/uk/pricing',
    AU: 'https://www.trybooking.com/au/pricing',
    US: 'https://www.trybooking.com/us/pricing'
  },
  tailor: {
    EU: 'https://www.tickettailor.com/pricing/',
    AU: 'https://www.tickettailor.com/pricing/',
    US: 'https://www.tickettailor.com/pricing/'
  }
};

const COMPETITOR_MATRIX = {
  eventbrite: {
    name: 'Eventbrite',
    EU: { rate: 0.059, flat: 0.79, includesProcessing: true },
    AU: { rate: 0.0535, flat: 1.19, includesProcessing: true },
    US: { rate: 0.037, flat: 1.79, includesProcessing: false, stripeRate: 0.029, stripeFlat: 0 }
  },
  more: {
    name: 'More.com',
    EU: { rate: 0.024, flat: 0.24, includesProcessing: true },
    AU: { rate: 0.024, flat: 0.24, includesProcessing: true },
    US: { rate: 0.024, flat: 0.24, includesProcessing: true }
  },
  humanitix: {
    name: 'Humanitix',
    EU: { rate: 0.021, flat: 0.99, includesProcessing: false, stripeRate: 0.015, stripeFlat: 0.25 },
    AU: { rate: 0.04, flat: 0.99, includesProcessing: true },
    US: { rate: 0.05, flat: 1.29, includesProcessing: true }
  },
  trybooking: {
    name: 'TryBooking',
    EU: { rate: 0.05, flat: 0.15, includesProcessing: true },
    AU: { rate: 0, flat: 0.50, includesProcessing: false, stripeRate: 0.025, stripeFlat: 0 },
    US: { rate: 0, flat: 0.50, includesProcessing: false, stripeRate: 0.025, stripeFlat: 0 }
  },
  tailor: {
    name: 'Ticket Tailor',
    EU: { rate: 0, flat: 0.70, includesProcessing: false, stripeRate: 0.015, stripeFlat: 0.25 },
    AU: { rate: 0, flat: 1.15, includesProcessing: false, stripeRate: 0.0175, stripeFlat: 0.30 },
    US: { rate: 0, flat: 0.75, includesProcessing: false, stripeRate: 0.029, stripeFlat: 0.30 }
  },
  custom: {
    name: 'Custom Fees',
    EU: { rate: 0, flat: 0, includesProcessing: false },
    AU: { rate: 0, flat: 0, includesProcessing: false },
    US: { rate: 0, flat: 0, includesProcessing: false }
  }
};

const PLANS = {
  starter: { name: 'Starter', rate: 0.0399, flat: 0.69 },
  premium: { name: 'Premium', rate: 0.0299, flat: 0.49 },
  pro: { name: 'Pro', rate: 0.02, flat: 0.29 }
};

const STRIPE_DEFAULTS = {
  EU: { rate: 0.015, flat: 0.25 },
  AU: { rate: 0.0175, flat: 0.30 },
  US: { rate: 0.029, flat: 0.30 }
};

const PricingCalculator = () => {
  const [selectedRegion, setSelectedRegion] = useState('EU');
  const [price, setPrice] = useState(50);
  const [quantity, setQuantity] = useState(500);
  const [selectedComp, setSelectedComp] = useState('eventbrite');
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const [strategy, setStrategy] = useState('absorb');
  
  const [customRate, setCustomRate] = useState(5);
  const [customFlat, setCustomFlat] = useState(50);

  const floorToTwo = (val) => Math.floor(val * 100) / 100;

  // Helper to format numbers based on region
  const formatValue = (val, decimals = 2) => {
    const locale = REGIONS[selectedRegion].locale;
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(val);
  };

  const stats = useMemo(() => {
    const revenue = price * quantity;
    const regionConfig = COMPETITOR_MATRIX[selectedComp][selectedRegion];
    
    const calculateCompetitorFees = () => {
      let platformFee = 0;
      let processingFee = 0;

      if (selectedComp === 'custom') {
        platformFee = (revenue * (customRate / 100)) + (quantity * (customFlat / 100));
        processingFee = (revenue * STRIPE_DEFAULTS[selectedRegion].rate) + (quantity * STRIPE_DEFAULTS[selectedRegion].flat);
      } else {
        platformFee = (revenue * regionConfig.rate) + (quantity * regionConfig.flat);
        
        if (!regionConfig.includesProcessing) {
          const stripeRate = regionConfig.stripeRate ?? STRIPE_DEFAULTS[selectedRegion].rate;
          const stripeFlat = regionConfig.stripeFlat ?? STRIPE_DEFAULTS[selectedRegion].flat;
          processingFee = (revenue * stripeRate) + (quantity * stripeFlat);
        }
      }

      return { platformFee, processingFee, total: platformFee + processingFee };
    };

    const compFees = calculateCompetitorFees();
    const plan = PLANS[selectedPlan];
    
    const bmiTotalFees = (revenue * plan.rate) + (quantity * plan.flat);
    
    let organizerCost = 0;
    let attendeeExtra = 0;

    if (strategy === 'absorb') {
      organizerCost = bmiTotalFees;
      attendeeExtra = 0;
    } else if (strategy === 'split') {
      organizerCost = bmiTotalFees / 2;
      attendeeExtra = bmiTotalFees / 2;
    } else if (strategy === 'pass') {
      organizerCost = 0;
      attendeeExtra = bmiTotalFees;
    }

    const savings = compFees.total - organizerCost;
    const netRevenue = revenue - organizerCost;

    return {
      revenue,
      netRevenue,
      compPlatformFee: floorToTwo(compFees.platformFee),
      compProcessingFee: floorToTwo(compFees.processingFee),
      competitorTotal: floorToTwo(compFees.total),
      bookMeInTotal: floorToTwo(bmiTotalFees),
      organizerCost: floorToTwo(organizerCost),
      attendeeExtra: floorToTwo(attendeeExtra),
      savings: floorToTwo(savings),
      margin: floorToTwo((savings / revenue) * 100),
      perTicketComp: floorToTwo(compFees.total / quantity),
      perTicketBmi: floorToTwo(bmiTotalFees / quantity),
      perTicketAttendeeExtra: floorToTwo(attendeeExtra / quantity)
    };
  }, [price, quantity, selectedComp, selectedRegion, customRate, customFlat, selectedPlan, strategy]);

  const symbol = REGIONS[selectedRegion].symbol;

  const sliderClasses = "w-full h-3 bg-slate-200 rounded-2xl appearance-none cursor-pointer accent-fuchsia-600 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-7 [&::-webkit-slider-thumb]:h-7 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-fuchsia-600 [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-[0_0_0_2px_#c026d3] [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:hover:scale-110 [&::-moz-range-thumb]:w-7 [&::-moz-range-thumb]:h-7 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-fuchsia-600 [&::-moz-range-thumb]:border-4 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-[0_0_0_2px_#c026d3] [&::-moz-range-thumb]:transition-all [&::-moz-range-thumb]:hover:scale-110";

  return (
    <>
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* Controls Panel */}
            <div className="lg:col-span-5 space-y-10">
              <div style={{
      paddingTop: '20px',
      paddingBottom: '20px'
    }} className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 shadow-sm">
                <div className="space-y-12">
                  {/* Region Selection */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Select Region</label>
                    <div className="flex bg-white p-1 rounded-2xl border border-slate-200">
                      {Object.entries(REGIONS).map(([key, region]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedRegion(key)}
                          className={`flex-1 py-3 px-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${
                            selectedRegion === key 
                              ? 'bg-indigo-600 text-white shadow-lg' 
                              : 'text-slate-400 hover:text-slate-600'
                          }`}
                        >
                          {region.label} ({region.symbol})
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Competitor Toggle */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Compare Against</label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(COMPETITOR_MATRIX).map(([key, comp]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedComp(key)}
                          className={`py-3 px-2 rounded-2xl text-[10px] font-bold uppercase tracking-wider transition-all border ${
                            selectedComp === key 
                              ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {comp.name}
                        </button>
                      ))}
                    </div>

                    <AnimatePresence>
                      {selectedComp === 'custom' && (
                        <motion.div 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="grid grid-cols-2 gap-4 pt-4 overflow-hidden"
                        >
                          <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Fee Percentage (%)</label>
                            <div className="relative">
                              <input 
                                type="number" 
                                value={customRate} 
                                onChange={(e) => setCustomRate(Number(e.target.value))}
                                className="w-full pl-4 pr-8 py-3 rounded-2xl border border-slate-200 focus:border-indigo-500 outline-none text-sm font-bold bg-white"
                              />
                              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">%</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Flat Fee (Cents)</label>
                            <div className="relative">
                              <input 
                                type="number" 
                                value={customFlat} 
                                onChange={(e) => setCustomFlat(Number(e.target.value))}
                                className="w-full pl-4 pr-12 py-3 rounded-2xl border border-slate-200 focus:border-indigo-500 outline-none text-sm font-bold bg-white"
                              />
                              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-[10px] uppercase">Cents</span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Price Slider */}
                  <div className="space-y-6">
                    <div className="flex justify-between items-end">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Average Ticket Price</label>
                      <span className="text-3xl font-black text-slate-900">{symbol} {formatValue(price)}</span>
                    </div>
                    <input 
                      type="range" min="5" max="1000" step="2" value={price}
                      onChange={(e) => setPrice(Number(e.target.value))}
                      className={sliderClasses}
                    />
                  </div>

                  {/* Quantity Slider */}
                  <div className="space-y-6">
                    <div className="flex justify-between items-end">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Total Attendees</label>
                      <span className="text-3xl font-black text-slate-900">{formatValue(quantity, 0)}</span>
                    </div>
                    <input 
                      type="range" min="50" max="10000" step="50" value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                      className={sliderClasses}
                    />
                  </div>

                  <FeeManagement selectedStrategy={strategy} onSelect={setStrategy} />
                </div>
              </div>
            </div>

            {/* Results Dashboard */}
            <div className="lg:col-span-7 mt-12 md:mt-16">
              <div className="relative">
                <motion.div style={{
        paddingTop: '55px',
        paddingBottom: '55px'
      }} 
                  layout
                  className="relative p-10 bg-slate-950 rounded-[3rem] shadow-2xl overflow-hidden border border-white/10"
                >
                  <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/20 blur-[100px] -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-600/10 blur-[100px] translate-y-1/2 -translate-x-1/2" />

                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-12">
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-400 mb-2 block">YOu SAVE with US</span>
                        <h3 style={{ color: 'white', fontSize: '70px' }} className="text-6xl md:text-8xl font-black tracking-tighter italic">
                          {symbol} {formatValue(stats.savings)}
                        </h3>
                      </div>
                      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4 text-center">
                        <p className="text-[10px] font-black uppercase text-indigo-300 mb-1">Extra Margin</p>
                        <p className={`text-2xl font-black ${stats.margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {stats.margin >= 0 ? '+' : ''}{formatValue(stats.margin)}%
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Competitor Breakdown */}
                      <div className="p-6 bg-white/5 rounded-3xl border border-white/5 flex flex-col justify-between">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4">You pay {selectedComp === 'custom' ? 'Custom Platform' : COMPETITOR_MATRIX[selectedComp].name}</p>
                          <div className="space-y-1 mb-6">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Platform Fees:</span>
                              <span className="text-white font-bold">{symbol} {formatValue(stats.compPlatformFee)}</span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Processing Fees:</span>
                              <span className="text-white font-bold">{symbol} {formatValue(stats.compProcessingFee)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-4 border-t border-white/5">
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-white">{symbol} {formatValue(stats.competitorTotal)}</span>
                            <span className="text-[10px] text-slate-500 uppercase font-black">Total Fees</span>
                          </div>
                          <p className="text-[9px] text-slate-600 font-bold uppercase mt-1">
                            Approx. {symbol} {formatValue(stats.perTicketComp)} per ticket
                          </p>
                        </div>
                      </div>
                      
                      {/* BookMeIn Breakdown */}
                      <div className="p-6 bg-indigo-600/10 rounded-3xl border border-indigo-500/20 flex flex-col justify-between">
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400">You pay BookMeIn.Events</p>
                          </div>
                          
                          <div className="flex gap-1 mb-4 bg-black/20 p-1 rounded-xl">
                            {Object.entries(PLANS).map(([key, plan]) => (
                              <button
                                key={key}
                                onClick={() => setSelectedPlan(key)}
                                className={`flex-1 py-1.5 px-1 rounded-lg text-[9px] font-bold uppercase tracking-tight transition-all ${
                                  selectedPlan === key 
                                    ? 'bg-indigo-600 text-white shadow-sm' 
                                    : 'text-slate-400 hover:text-white'
                                }`}
                              >
                                {plan.name}
                              </button>
                            ))}
                          </div>
                          
                          <div className="text-[11px] text-indigo-300 mb-4 font-bold uppercase tracking-wider">
                            Base Fee: {formatValue(PLANS[selectedPlan].rate * 100)}% + {symbol} {formatValue(PLANS[selectedPlan].flat)}
                          </div>

                          <div className="space-y-1 mb-6">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Your Share:</span>
                              <span className="text-white font-bold">{symbol} {formatValue(stats.organizerCost)}</span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Attendee Share:</span>
                              <span className="text-white font-bold">{symbol} {formatValue(stats.attendeeExtra)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-indigo-500/10">
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-white">{symbol} {formatValue(stats.organizerCost)}</span>
                            <span className="text-[10px] text-indigo-300 uppercase font-black">Your Total Cost</span>
                          </div>
                          <p className="text-[9px] text-indigo-400/60 mt-1 font-bold uppercase">
                            Approx. {symbol} {formatValue(stats.perTicketBmi)} per ticket
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-10 pt-10 border-t border-white/10 flex flex-col gap-6">
                      <p className="text-[11px] text-slate-400 font-medium w-full">
                        *Calculations based on {COMPETITOR_MATRIX[selectedComp].name} pricing for {REGIONS[selectedRegion].label}.
                        Includes regional payment processing costs where applicable.
                      </p>
                      {selectedComp !== 'custom' && PRICING_URLS[selectedComp]?.[selectedRegion] && (
                        <a 
                          href={PRICING_URLS[selectedComp][selectedRegion]} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-indigo-400 hover:text-indigo-300 transition-colors group w-fit"
                        >
                          Verify {COMPETITOR_MATRIX[selectedComp].name} Pricing 
                          <SafeIcon icon={FiIcons.FiExternalLink} className="w-3 h-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>

                <div 
                  style={{
        marginLeft: '5px',
        marginRight: '5px',
        paddingTop: '38px',
        paddingBottom: '38px'
      }} 
                  className="absolute -top-24 left-[54%] -translate-x-1/2 bg-white border-4 border-fuchsia-600 shadow-2xl px-11 py-9 rounded-[3rem] rotate-[-2deg] hidden md:block z-20 whitespace-nowrap scale-105 transform transition-transform"
                >
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-fuchsia-600 mb-2 text-center">YOUR Net Take-Home</p>
                  <p className="text-4xl font-black text-slate-900 tracking-tighter text-center">{symbol} {formatValue(stats.netRevenue)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* 1px Height Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-px w-full bg-slate-200" />
      </div>
    </>
  );
};

export default PricingCalculator;